import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Role, User } from "@/services/types/auth";

export interface TokenSet {
  accessToken: string;
  refreshToken: string;
  /** seconds-until-expiry from backend `expires_in` */
  expiresIn: number;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  /** epoch ms · 由 expiresIn 计算 */
  expiresAt: number | null;

  loginSuccess: (user: User, tokens: TokenSet) => void;
  setUser: (user: User) => void;
  /** RBAC-ENF1 · /me 刷新后只更新权限 / 角色码，不动用户手动切换的当前角色 */
  setPermissions: (permissions: string[], roleCodes?: User["roleCodes"], roleLabels?: Record<string, string>) => void;
  setTokens: (tokens: TokenSet) => void;
  logout: () => void;
  switchRole: (role: Role) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,

      loginSuccess: (user, tokens) =>
        set({
          user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: Date.now() + tokens.expiresIn * 1000,
        }),

      setUser: (user) => set({ user }),

      setPermissions: (permissions, roleCodes, roleLabels) => {
        const u = get().user;
        if (!u) return;
        set({ user: { ...u, permissions, ...(roleCodes ? { roleCodes } : {}), ...(roleLabels ? { roleLabels } : {}) } });
      },

      setTokens: (tokens) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          expiresAt: Date.now() + tokens.expiresIn * 1000,
        }),

      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
        }),

      switchRole: (role) => {
        const u = get().user;
        if (!u) return;
        set({ user: { ...u, role } });
      },
    }),
    { name: "ota-auth" },
  ),
);
